import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { PenLine, Disc3, Headphones, Wand2, SlidersHorizontal, Library, Trophy, Share2 } from 'lucide-react';
import { api } from '@/lib/api';
import { StyleCard } from '@/components/cards';
import { STYLES } from '@/data/styles';

export function SectionHead({ kicker, title, text }) {
  return (
    <div className="mx-auto mb-12 max-w-2xl text-center">
      <p className="font-mono text-xs uppercase tracking-[0.25em] text-brand">{kicker}</p>
      <h2 className="mt-3 font-display text-3xl font-bold tracking-tight text-white sm:text-4xl">{title}</h2>
      {text && <p className="mt-4 text-base text-white/60">{text}</p>}
    </div>
  );
}

const STEPS = [
  [PenLine, 'Décris ton idée', 'Une histoire, une émotion, un prénom, une occasion : écris-la avec tes mots.'],
  [Disc3, 'Choisis ton style', 'Gospel, Zouglou, Coupé-Décalé, RnB, Drill… sélectionne l’univers qui te ressemble.'],
  [Headphones, 'Génère et écoute', 'MyMusic IA compose ta chanson originale. Écoute-la, télécharge-la, partage-la.'],
];

export function HowItWorks() {
  return (
    <section id="comment" className="scroll-mt-24 py-24" data-testid="landing-how">
      <div className="mx-auto max-w-6xl px-5 sm:px-8">
        <SectionHead kicker="Comment ça marche" title="Trois étapes, une chanson qui te ressemble." />
        <div className="grid gap-5 md:grid-cols-3">
          {STEPS.map(([Icon, t, d], i) => (
            <div key={t} className="card-surface group relative overflow-hidden p-8 transition-colors hover:border-brand/40" data-testid={`how-step-${i + 1}`}>
              <span className="absolute right-6 top-4 font-display text-7xl font-black text-white/[0.04]">0{i + 1}</span>
              <span className="flex h-12 w-12 items-center justify-center rounded-2xl bg-brand/15 text-brand transition-transform group-hover:scale-110"><Icon className="h-6 w-6" /></span>
              <h3 className="mt-6 font-display text-xl font-bold text-white">{t}</h3>
              <p className="mt-2 text-sm leading-relaxed text-white/60">{d}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

export function StylesShowcase() {
  const navigate = useNavigate();
  const [styles, setStyles] = useState(STYLES);
  useEffect(() => {
    api.get('/styles').then(({ data }) => data?.length && setStyles(data)).catch(() => {});
  }, []);
  return (
    <section id="styles" className="scroll-mt-24 py-24" data-testid="landing-styles">
      <div className="mx-auto max-w-6xl px-5 sm:px-8">
        <SectionHead kicker={`${styles.length} styles musicaux`} title="Tous les sons de ton imagination."
                     text="Des rythmes ivoiriens aux sonorités urbaines internationales, choisis le style de ta prochaine chanson." />
        <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-5">
          {styles.map((s) => <StyleCard key={s.id || s.slug} style={s} onClick={() => navigate(`/create?style=${s.slug}`)} />)}
        </div>
      </div>
    </section>
  );
}

const FEATURES = [
  [Wand2, 'Création musicale IA', 'Paroles, mélodie et voix générées à partir de ta description.'],
  [SlidersHorizontal, 'Studio', 'Organise tes projets et retravaille tes morceaux dans un espace dédié.'],
  [Library, 'Bibliothèque personnelle', 'Toutes tes créations au même endroit, privées par défaut.'],
  [Trophy, 'Challenges musicaux', 'Publie tes meilleurs titres, récolte des votes et grimpe au classement.'],
  [Share2, 'Partage', 'Un lien pour faire écouter tes chansons publiques à tes proches.'],
];

export function Features() {
  return (
    <section id="fonctionnalites" className="scroll-mt-24 py-24" data-testid="landing-features">
      <div className="mx-auto max-w-6xl px-5 sm:px-8">
        <SectionHead kicker="Fonctionnalités" title="Un studio complet, dans ta poche." />
        <div className="grid gap-px overflow-hidden rounded-3xl border border-white/5 bg-white/5 sm:grid-cols-2 lg:grid-cols-5">
          {FEATURES.map(([Icon, t, d]) => (
            <div key={t} className="bg-surface-app p-7 transition-colors hover:bg-surface-card">
              <Icon className="h-6 w-6 text-brand" />
              <h3 className="mt-5 font-display text-lg font-bold text-white">{t}</h3>
              <p className="mt-2 text-sm leading-relaxed text-white/55">{d}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
